import React, { useState } from 'react';
import './PopupForm.css';

const PopupForm = ({ onClose, onDownload, selectedMenu, selectedSubMenu }) => {
  const [formState, setFormState] = useState({
    name: '',
    companyName: '',
    email: '',
    mobileNumber: '',
    otp: '',
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for button control

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMobileNumber = (mobileNumber) => {
    const mobileRegex = /^[6-9]\d{9}$/; // Adjusted regex for Indian mobile numbers
    return mobileRegex.test(mobileNumber);
  };

  const validateForm = () => {
    const { name, companyName, email, mobileNumber } = formState;
    if (!name.trim()) {
      setError('Name is required.');
      return false;
    }
    if (!companyName.trim()) {
      setError('Company name is required.');
      return false;
    }
    if (!email.trim() || !validateEmail(email)) {
      setError('A valid email is required.');
      return false;
    }
    if (!mobileNumber.trim() || !validateMobileNumber(mobileNumber)) {
      setError('A valid mobile number is required.');
      return false;
    }
    setError('');
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleOtpSend = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_CONNECTION_URL}/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobileNumber: formState.mobileNumber }),
      });
      if (response.ok) {
        setOtpSent(true);
        setSuccess('An OTP is sent on entered mobile number.');
        setError('');
      } else {
        setError('Error sending OTP.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    }
  };

  const handleOtpVerify = async () => {
    if (!formState.otp.trim()) {
      setError('Please enter the OTP.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_CONNECTION_URL}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobileNumber: formState.mobileNumber, otp: formState.otp }),
      });
      const result = await response.json();
      if (result.success) {
        setOtpVerified(true);
        setSuccess('OTP verified successfully.');
        setError('');
      } else {
        setError('Invalid OTP.');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true); // Disable the button on submit

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_CONNECTION_URL}/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          selectedMenu,
          selectedSubMenu,
          formType: 'downloadDemo',
        }),
      });

      if (response.ok) {
        setSuccess('Form submitted successfully.');
        setError('');
        setTimeout(async () => {
          setSuccess('Please wait... Your download will start shortly.');
          await onDownload(selectedMenu, selectedSubMenu);
        }, 1000);
      } else {
        setError('Error submitting form.');
        setIsSubmitting(false); // Re-enable the button if there's an error
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      setIsSubmitting(false); // Re-enable the button on error
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!otpSent) {
        handleOtpSend();
      } else if (otpSent && !otpVerified) {
        handleOtpVerify();
      } else if (otpVerified) {
        handleSubmit(e);
      }
    }
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="popup-container" onClick={onClose}>
      <div className="popup-content" onClick={handleContainerClick}>
        <form onSubmit={handleSubmit} className="popup-form" onKeyPress={handleKeyPress}>
          <h2>Download Demo</h2>
          <label>
            Name:
            <input
              type="text"
              name="name"
              value={formState.name}
              onChange={handleChange}
              required
            />
          </label>
          <label>
            Company Name:
            <input
              type="text"
              name="companyName"
              value={formState.companyName}
              onChange={handleChange}
              required
            />
          </label>
          <label>
            Email:
            <input
              type="email"
              name="email"
              value={formState.email}
              onChange={handleChange}
              required
            />
          </label>
          <label>
            Mobile Number:
            <input
              type="text"
              name="mobileNumber"
              value={formState.mobileNumber}
              onChange={handleChange}
              required
            />
          </label>

          {!otpSent && (
            <button type="button" onClick={handleOtpSend} className='submit-button'>
              Send OTP
            </button>
          )}

          {otpSent && !otpVerified && (
            <>
              <label>
                OTP:
                <input
                  type="text"
                  name="otp"
                  value={formState.otp}
                  onChange={handleChange}
                  required
                />
              </label>
              <button type="button" onClick={handleOtpVerify} className='submit-button'>
                Verify OTP
              </button>
            </>
          )}

          {otpVerified && (
            <button type="submit" className='submit-button' disabled={isSubmitting}>
              {isSubmitting ? 'Downloading...' : 'Submit'}
            </button>
          )}

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}
          <button className="popup-close-button" onClick={onClose}>x</button>
        </form>
      </div>
    </div>
  );
};

export default PopupForm;
